import type { EtmfFolder, EtmfDocument } from "@/api/protocol-types";

export interface EtmfCompletionStats {
  totalExpected: number;
  totalRequired: number;
  totalFiled: number;
  requiredFiled: number;
  completionPercent: number;
  requiredCompletionPercent: number;
  byZone: Record<string, { expected: number; filed: number; required: number; requiredFiled: number }>;
  byFolder: Record<string, { expected: number; filed: number; missingRequired: string[] }>;
}

export function computeEtmfCompletion(
  folders: EtmfFolder[],
  documents: EtmfDocument[],
): EtmfCompletionStats {
  let totalExpected = 0;
  let totalRequired = 0;
  let totalFiled = 0;
  let requiredFiled = 0;
  const byZone: EtmfCompletionStats["byZone"] = {};
  const byFolder: EtmfCompletionStats["byFolder"] = {};

  // Index documents by folderId + expectedDocName
  const docIndex = new Map<string, EtmfDocument[]>();
  for (const doc of documents) {
    const key = `${doc.folderId}::${doc.expectedDocName}`;
    if (!docIndex.has(key)) docIndex.set(key, []);
    docIndex.get(key)!.push(doc);
  }

  for (const folder of folders) {
    const zone = folder.zone ?? "Other";
    if (!byZone[zone]) byZone[zone] = { expected: 0, filed: 0, required: 0, requiredFiled: 0 };
    const folderStats = { expected: 0, filed: 0, missingRequired: [] as string[] };

    for (const expectedDoc of (folder.expectedDocuments ?? [])) {
      totalExpected++;
      byZone[zone].expected++;
      folderStats.expected++;

      if (expectedDoc.required) {
        totalRequired++;
        byZone[zone].required++;
      }

      const key = `${folder.id}::${expectedDoc.name}`;
      const filed = docIndex.has(key);

      if (filed) {
        totalFiled++;
        byZone[zone].filed++;
        folderStats.filed++;
        if (expectedDoc.required) {
          requiredFiled++;
          byZone[zone].requiredFiled++;
        }
      } else if (expectedDoc.required) {
        folderStats.missingRequired.push(expectedDoc.name);
      }
    }

    byFolder[folder.id] = folderStats;
  }

  return {
    totalExpected,
    totalRequired,
    totalFiled,
    requiredFiled,
    completionPercent: totalExpected > 0 ? Math.round((totalFiled / totalExpected) * 100) : 0,
    requiredCompletionPercent: totalRequired > 0 ? Math.round((requiredFiled / totalRequired) * 100) : 0,
    byZone,
    byFolder,
  };
}

export function suggestFolder(
  filename: string,
  folders: EtmfFolder[],
): { folderId: string; expectedDocName: string } | null {
  const normalizedFilename = filename.toLowerCase().replace(/[._-]/g, " ");

  let bestMatch: { folderId: string; expectedDocName: string; score: number } | null = null;

  for (const folder of folders) {
    for (const expectedDoc of (folder.expectedDocuments ?? [])) {
      const docWords = expectedDoc.name.toLowerCase().replace(/[._-]/g, " ").split(/\s+/).filter(Boolean);
      if (docWords.length === 0) continue;

      const matchCount = docWords.filter((word) => normalizedFilename.includes(word)).length;
      const score = matchCount / docWords.length;

      if (score > 0.5 && (!bestMatch || score > bestMatch.score)) {
        bestMatch = { folderId: folder.id, expectedDocName: expectedDoc.name, score };
      }
    }
  }

  return bestMatch ? { folderId: bestMatch.folderId, expectedDocName: bestMatch.expectedDocName } : null;
}

export function getDocumentsForFolder(
  folderId: string,
  documents: EtmfDocument[],
): EtmfDocument[] {
  return documents.filter((d) => d.folderId === folderId);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
